import {useCallback} from 'react';
import memoize from 'nano-memoize';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {handlePluralMessage} from '@ui/i18n/handle-plural-message';
import {MessageDescriptor} from '@ui/i18n/message-descriptor';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {isSsr} from '@ui/utils/dom/is-ssr';

export interface UseTransReturn {
  trans: (props: MessageDescriptor) => string;
}

export function useTrans(): UseTransReturn {
  const {lines, localeCode} = useSelectedLocale();
  const trans = useCallback(
    (props: MessageDescriptor): string => {
      return translate({...props, lines, localeCode});
    },
    [lines, localeCode],
  );

  return {trans};
}

interface TranslateProps extends MessageDescriptor {
  lines?: Record<string, string>;
  localeCode: string;
}
function translateUncached(props: TranslateProps): string {
  let {lines, message, values, localeCode} = props;

  if (message == null) {
    return '';
  }

  message = lines?.[message] || lines?.[message.toLowerCase()] || message;

  if (!values) {
    return message;
  }

  // Çeviri bulunduktan SONRA pluralize et: <Trans> gibi ÇEVRİLMİŞ message'ı
  // geç (props.message ORİJİNAL/çevrilmemiş anahtar → çeviriyi ezip kaynak
  // dile döndürüyordu; values'lu tüm trans() çağrıları non-TR'de TR sızdırıyordu).
  message = handlePluralMessage(localeCode, {message, values});

  Object.entries(values).forEach(([key, value]) => {
    message = message.replace(`:${key}`, `${value}`);
  });

  return message;
}

// SSR BELLEK SIZINTISI: önbellek anahtarı `lines`i, yani TÜM çeviri sözlüğünü
// (~170 KB) taşır. Her istek bootstrapData'yı yeniden ayrıştırdığı için `lines`
// her render'da yeni bir nesnedir → önbellek hiç isabet etmez, yalnız büyür ve
// her render'ın sözlüğünü kalıcı olarak tutar. Üstelik nano-memoize `equals`
// verildiğinde çok-argümanlı koluna düşer: sınırsız dizi + her çağrıda doğrusal
// tarama, maxAge yok. Ölçüm: render başına 328 KB kalıcı tutulma; süreç ~3000
// render'da heap tavanına çarpıp node::OOMErrorHandler ile ölüyordu.
// Tarayıcıda `lines` oturum boyunca SABİT olduğu için önbellek anlamlıdır ve
// aynen korunur; ayrım import.meta.env.SSR ile derleme zamanında yapılır.
const translate: (props: TranslateProps) => string = isSsr()
  ? translateUncached
  : memoize(translateUncached, {equals: shallowEqual, callTimeout: 0});
